import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { FileText, Plus, Eye, DollarSign, Download, Link2, Search, X as XIcon, SlidersHorizontal, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { ActionMenu } from '../../components/crm/CrmListShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SEARCH = {
  q: '',
  customer_name: '',
  customer_email: '',
  booking_id: '',
  invoice_id: '',
  date_from: '',
  date_to: '',
  date_field: 'party_date',
};

export const CrmInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [search, setSearch] = useState(EMPTY_SEARCH);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const buildParams = useCallback((statusFilter, s) => {
    const params = {};
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (s.q?.trim()) params.q = s.q.trim();
    if (s.customer_name?.trim()) params.customer_name = s.customer_name.trim();
    if (s.customer_email?.trim()) params.customer_email = s.customer_email.trim();
    if (s.booking_id?.trim()) params.booking_id = s.booking_id.trim();
    if (s.invoice_id?.trim()) params.invoice_id = s.invoice_id.trim();
    if (s.date_from) params.date_from = s.date_from;
    if (s.date_to) params.date_to = s.date_to;
    if (s.date_field && s.date_field !== 'party_date') params.date_field = s.date_field;
    return params;
  }, []);

  const fetchInvoices = useCallback(async (statusFilter, s) => {
    setLoading(true);
    try {
      const params = buildParams(statusFilter, s);
      const res = await axios.get(`${API_URL}/api/admin/invoices`, {
        params, withCredentials: true,
      });
      setInvoices(res.data);
    } catch (e) { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [buildParams]);

  // Single debounced effect: refetch whenever status or search change.
  // 300ms debounce keeps free-text typing snappy without thrashing the API.
  useEffect(() => {
    const t = setTimeout(() => { fetchInvoices(filter, search); }, 300);
    return () => clearTimeout(t);
  }, [filter, search, fetchInvoices]);

  // No-op explicit triggers — the debounced useEffect above handles refetch
  // automatically whenever `filter` or `search` change. Keeping these stubs
  // so the "Apply" / "Clear" buttons still wire up cleanly.
  const applyAdvanced = () => fetchInvoices(filter, search);
  const clearSearch = () => setSearch(EMPTY_SEARCH);

  const hasActiveSearch = (
    search.q || search.customer_name || search.customer_email ||
    search.booking_id || search.invoice_id || search.date_from || search.date_to
  );

  const openGenerate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/bookings`, { withCredentials: true });
      setBookings(res.data);
      setGenerateOpen(true);
    } catch (e) { toast.error('Failed to load bookings'); }
  };

  const generateInvoice = async (bookingId) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/invoices/generate`, { booking_id: bookingId }, { withCredentials: true });
      toast.success(`Invoice ${res.data.invoice_id} generated`);
      setGenerateOpen(false);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate invoice'); }
  };

  const viewInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/invoices/${invoiceId}`, { withCredentials: true });
      setSelected(res.data);
      setViewOpen(true);
    } catch (e) { toast.error('Failed'); }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const copyPublicLink = async (bookingId) => {
    const link = `${window.location.origin}/invoice/${bookingId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Customer invoice link copied');
    } catch {
      toast.info(link);
    }
  };

  const openEmailDialog = () => {
    if (!selected) return;
    setEmailTo(selected.customer_email || '');
    setEmailNote('');
    setEmailOpen(true);
  };

  const sendInvoiceEmail = async () => {
    if (!selected) return;
    if (!emailTo || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailTo)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/invoices/${selected.invoice_id}/email`,
        { to_email: emailTo, note: emailNote },
        { withCredentials: true }
      );
      toast.success(`Invoice emailed to ${res.data.to}`);
      setEmailOpen(false);
      // Refresh selected so "Last emailed at" shows up next time the dialog opens
      try {
        const fresh = await axios.get(`${API_URL}/api/admin/invoices/${selected.invoice_id}`, { withCredentials: true });
        setSelected(fresh.data);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to email invoice');
    } finally {
      setEmailSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = { paid: 'bg-green-100 text-green-700', partial: 'bg-yellow-100 text-yellow-700', unpaid: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${map[status] || map.unpaid}`}>{status}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Invoices</h1>
          <p className="text-[#6B6B7A]" data-testid="invoice-count">
            {invoices.length} invoice{invoices.length === 1 ? '' : 's'}{hasActiveSearch ? ' match your search' : ' total'}
          </p>
        </div>
        <Button onClick={openGenerate} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="generate-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> Generate Invoice
        </Button>
      </div>

      {/* Search + Filter */}
      <div className="mb-6 space-y-3" data-testid="invoice-search-bar">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Free-text search */}
          <div className="relative flex-1 min-w-[260px] max-w-[420px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
            <Input
              placeholder="Search invoice #, booking, customer, email, child…"
              value={search.q}
              onChange={(e) => setSearch((s) => ({ ...s, q: e.target.value }))}
              className="pl-10 pr-9 bg-white rounded-xl border-[#E8E5DF]"
              data-testid="invoice-search-input"
            />
            {search.q && (
              <button
                type="button"
                onClick={() => setSearch((s) => ({ ...s, q: '' }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-[#6B6B7A] hover:bg-[#FFF0F3] hover:text-[#FF3B6B]"
                aria-label="Clear search"
                data-testid="invoice-search-clear-q"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 rounded-xl border-[#E8E5DF] bg-white" data-testid="invoice-status-filter">
              <SelectValue placeholder="All Invoices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="unpaid">Unpaid</SelectItem>
            </SelectContent>
          </Select>

          {/* Advanced filters toggle */}
          <Button
            type="button" variant="outline"
            onClick={() => setAdvancedOpen((v) => !v)}
            className={`rounded-xl border-[#E8E5DF] ${advancedOpen ? 'bg-[#1E1E24] text-white border-[#1E1E24]' : 'bg-white'}`}
            data-testid="invoice-advanced-toggle"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Advanced
            {hasActiveSearch && !advancedOpen && (
              <span className="ml-2 w-2 h-2 rounded-full bg-[#FF3B6B]" aria-hidden />
            )}
          </Button>

          {hasActiveSearch && (
            <Button
              type="button" variant="ghost"
              onClick={clearSearch}
              className="rounded-xl text-[#6B6B7A] hover:text-[#D1344D]"
              data-testid="invoice-search-clear-all"
            >
              <XIcon className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Advanced filter panel */}
        {advancedOpen && (
          <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="invoice-advanced-panel">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Customer name</label>
              <Input
                value={search.customer_name}
                onChange={(e) => setSearch((s) => ({ ...s, customer_name: e.target.value }))}
                placeholder="e.g. Sarah Lee"
                className="bg-white"
                data-testid="invoice-filter-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Customer email</label>
              <Input
                value={search.customer_email}
                onChange={(e) => setSearch((s) => ({ ...s, customer_email: e.target.value }))}
                placeholder="@gmail.com"
                className="bg-white"
                data-testid="invoice-filter-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Booking ID</label>
              <Input
                value={search.booking_id}
                onChange={(e) => setSearch((s) => ({ ...s, booking_id: e.target.value }))}
                placeholder="booking_…"
                className="bg-white"
                data-testid="invoice-filter-booking"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Invoice #</label>
              <Input
                value={search.invoice_id}
                onChange={(e) => setSearch((s) => ({ ...s, invoice_id: e.target.value }))}
                placeholder="INV-…"
                className="bg-white"
                data-testid="invoice-filter-invnum"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Date from</label>
              <Input
                type="date"
                value={search.date_from}
                onChange={(e) => setSearch((s) => ({ ...s, date_from: e.target.value }))}
                className="bg-white"
                data-testid="invoice-filter-from"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Date to</label>
              <Input
                type="date"
                value={search.date_to}
                onChange={(e) => setSearch((s) => ({ ...s, date_to: e.target.value }))}
                className="bg-white"
                data-testid="invoice-filter-to"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B6B7A] font-semibold block mb-1">Date field</label>
              <Select value={search.date_field} onValueChange={(v) => setSearch((s) => ({ ...s, date_field: v }))}>
                <SelectTrigger className="bg-white" data-testid="invoice-filter-date-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="party_date">Party date</SelectItem>
                  <SelectItem value="created_at">Invoice created</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                type="button"
                onClick={applyAdvanced}
                className="rounded-xl bg-[#FF3B6B] hover:bg-[#E62E58] text-white flex-1"
                data-testid="invoice-filter-apply"
              >
                Apply
              </Button>
              <Button
                type="button" variant="outline"
                onClick={clearSearch}
                className="rounded-xl border-[#E8E5DF]"
                data-testid="invoice-filter-reset"
              >
                Reset
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="hidden lg:table-cell">Package</TableHead>
              <TableHead className="hidden md:table-cell">Party Date</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="hidden md:table-cell text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#6B6B7A]" data-testid="invoice-empty">
                {hasActiveSearch ? 'No invoices match your search.' : 'No invoices found'}
              </TableCell></TableRow>
            ) : invoices.map(inv => (
              <TableRow key={inv.invoice_id}>
                <TableCell className="font-mono text-sm font-medium">{inv.invoice_id}</TableCell>
                <TableCell>
                  <p className="font-medium text-[#1E1E24]">{inv.customer_name}</p>
                  <p className="text-xs text-[#6B6B7A]">{inv.customer_email}</p>
                </TableCell>
                <TableCell className="hidden lg:table-cell">{inv.package_name}</TableCell>
                <TableCell className="hidden md:table-cell">{inv.party_date}</TableCell>
                <TableCell className="text-right font-semibold">${inv.total?.toFixed(2)}</TableCell>
                <TableCell className="hidden md:table-cell text-right text-[#06D6A0] font-medium">${inv.amount_paid?.toFixed(2)}</TableCell>
                <TableCell className="text-right text-[#FF3B6B] font-medium">${inv.balance_due?.toFixed(2)}</TableCell>
                <TableCell>{getStatusBadge(inv.status)}</TableCell>
                <TableCell className="text-right">
                  <ActionMenu
                    testid={`invoice-actions-${inv.invoice_id}`}
                    items={[
                      { label: 'View invoice', icon: Eye, onClick: () => viewInvoice(inv.invoice_id), testid: `view-inv-${inv.invoice_id}` },
                      { label: 'Download PDF', icon: Download, onClick: () => downloadPdf(inv.invoice_id), testid: `download-inv-${inv.invoice_id}` },
                      { label: 'Copy pay link', icon: Link2, onClick: () => copyPublicLink(inv.booking_id), testid: `copy-link-${inv.invoice_id}` },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* View Invoice Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Invoice Detail</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-lg font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>Creative Birthdays</p>
                  <p className="text-xs text-[#6B6B7A]">Invoice #{selected.invoice_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#6B6B7A]">Due: {selected.due_date}</p>
                  {getStatusBadge(selected.status)}
                </div>
              </div>
              <div className="border-t border-[#E8E5DF] pt-4">
                <p className="text-sm font-semibold">Bill To:</p>
                <p className="text-sm">{selected.customer_name}</p>
                <p className="text-xs text-[#6B6B7A]">{selected.customer_email} | {selected.customer_phone}</p>
              </div>
              <div className="border-t border-[#E8E5DF] pt-4">
                <div className="flex justify-between text-sm mb-1"><span>Package: {selected.package_name}</span><span>${selected.subtotal?.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm mb-1"><span>Tax (13%)</span><span>${selected.tax?.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t"><span>Total</span><span>${selected.total?.toFixed(2)}</span></div>
              </div>
              <div className="bg-[#FFFDF8] rounded-xl p-4 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-[#06D6A0]">Amount Paid</span><span className="text-[#06D6A0] font-medium">${selected.amount_paid?.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[#FF3B6B]">Balance Due</span><span className="text-[#FF3B6B] font-medium">${selected.balance_due?.toFixed(2)}</span></div>
              </div>
              <div className="text-sm text-[#6B6B7A]">
                <p>Party: {selected.child_name}&apos;s Birthday - {selected.party_date}</p>
                {selected.last_emailed_at && (
                  <p className="text-[11px] mt-1" data-testid="last-emailed-hint">
                    Last emailed to <strong className="text-[#1E1E24]">{selected.last_emailed_to}</strong> on {String(selected.last_emailed_at).replace('T', ' ').slice(0, 16)}
                  </p>
                )}
              </div>
              <Button onClick={openEmailDialog} className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="dialog-email-invoice">
                <Mail className="w-4 h-4 mr-2" /> Email Invoice to Client
              </Button>
              <Button onClick={() => downloadPdf(selected.invoice_id)} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="dialog-download-pdf">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              <Button onClick={() => copyPublicLink(selected.booking_id)} variant="outline" className="w-full rounded-full border-[#E8E5DF]" data-testid="dialog-copy-link">
                <Link2 className="w-4 h-4 mr-2" /> Copy customer pay link
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Invoice Dialog (iter-141) */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Email Invoice to Client</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-xs text-[#6B6B7A] bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3 leading-relaxed">
                The client will receive a styled email with the invoice <strong>PDF attached</strong>, a summary of the balance due, and a <strong>direct pay link</strong>. They can reply for questions.
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1E1E24]">Recipient *</label>
                <Input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="client@example.com"
                  className="mt-1 rounded-xl border-[#E8E5DF]"
                  data-testid="email-invoice-to"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#1E1E24]">Personal note (optional)</label>
                <textarea
                  value={emailNote}
                  onChange={(e) => setEmailNote(e.target.value)}
                  rows={3}
                  placeholder="Hi! Here's your invoice for the upcoming party — let us know if you have any questions."
                  className="mt-1 w-full rounded-xl border border-[#E8E5DF] p-3 text-sm focus:outline-none focus:border-[#FF3B6B]"
                  data-testid="email-invoice-note"
                />
              </div>
              <div className="text-[11px] text-[#6B6B7A]">
                Invoice <strong>{selected.invoice_id}</strong> · Balance due <strong className="text-[#FF3B6B]">${(selected.balance_due || 0).toFixed(2)}</strong>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEmailOpen(false)} className="flex-1 rounded-full" disabled={emailSending} data-testid="email-invoice-cancel">
                  Cancel
                </Button>
                <Button onClick={sendInvoiceEmail} className="flex-1 bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" disabled={emailSending} data-testid="email-invoice-send">
                  <Mail className="w-4 h-4 mr-2" /> {emailSending ? 'Sending…' : 'Send Invoice'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Dialog */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Generate Invoice from Booking</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.booking_id} className="flex justify-between items-center p-3 bg-[#FFFDF8] rounded-xl">
                <div>
                  <p className="font-medium text-sm">{b.child_name} - {b.package_name}</p>
                  <p className="text-xs text-[#6B6B7A]">{b.date} | {b.customer_name}</p>
                </div>
                <Button size="sm" onClick={() => generateInvoice(b.booking_id)}
                  className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full text-xs"
                  data-testid={`gen-inv-${b.booking_id}`}>
                  <FileText className="w-3 h-3 mr-1" /> Generate
                </Button>
              </div>
            ))}
            {bookings.length === 0 && <p className="text-center text-[#6B6B7A] py-4">No bookings available</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmInvoices;
